import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { type CmsPage, type CmsPagesListResponse } from '@/domain';
import { parseApiUtcDate } from '@/lib/date';
import { bootstrapAccount, getAccountId } from './auth';
import { api } from './client';
import { endpoints, type Endpoint } from './endpoints';
import { queryKeys, type QueryFilters } from './query-keys';

/** Minimum characters before calling sports search (matches classic client). */
export const SPORTS_SEARCH_MIN_LENGTH = 4;

export interface SearchCategoryDto {
  SportId: number;
  Sport: string;
  Id: number;
  Name: string;
  Blocked?: boolean;
}

export interface SearchTournamentDto {
  SportId: number;
  Sport: string;
  CategoryId: number;
  Category: string;
  Id: number;
  Name: string;
  Blocked?: boolean;
}

export interface SearchEventDto {
  SportId: number;
  Sport: string;
  CategoryId: number;
  Category: string;
  TournamentId: number;
  Tournament: string;
  Id: number;
  Home: string;
  Away: string;
  Date: string;
  Code?: string;
  Blocked?: boolean;
  BId?: number;
}

export interface SportsSearchResponse {
  Categories: SearchCategoryDto[] | null;
  Tournaments: SearchTournamentDto[] | null;
  Events: SearchEventDto[] | null;
}

export interface TournamentMenuItem {
  Id: number;
  Name: string;
  Position: number;
  Blocked: boolean;
  Sport: string;
  Category: string;
  Count: number;
}

export interface CategoryMenuItem {
  Id: number;
  Name: string;
  Position: number;
  Blocked: boolean;
  Sport: string;
  Tournaments: TournamentMenuItem[];
}

export interface SportMenuItem {
  Id: number;
  Name: string;
  Position: number;
  Blocked: boolean;
  Count: number;
  Categories: CategoryMenuItem[];
}

export interface MenuFilterDto {
  Range?: string;
  /** ISO date string */
  From?: string;
  /** ISO date string */
  To?: string;
}

const DEFAULT_MENU_FILTERS: MenuFilterDto = { Range: 'All' };

export function sportsMenuFullQuery(filters: MenuFilterDto = DEFAULT_MENU_FILTERS) {
  return queryOptions({
    queryKey: ['sports2', 'menu', 'full', filters],
    queryFn: ({ signal }) =>
      api.post<SportMenuItem[]>(
        endpoints.sports.menuFull.host,
        endpoints.sports.menuFull.path,
        filters as Record<string, unknown>,
        signal,
      ),
    staleTime: 5 * 60_000,
  });
}

/**
 * Thin `queryOptions` builders over the endpoint catalogue. Features import these
 * so a route loader and a component share one definition — `useQuery(...)` and
 * `queryClient.ensureQueryData(...)` cannot drift apart.
 *
 * Response types are intentionally loose for now: the payload shapes get modelled
 * per feature as the screens are built.
 */
function fetchFrom<T>(endpoint: Endpoint, query?: QueryFilters, signal?: AbortSignal) {
  return api.get<T>(endpoint.host, endpoint.path, query, signal);
}

/**
 * The brand configuration every other request depends on. It is a query so the root
 * route can await it and the devtools can show its state, but the token itself is
 * cached in the auth module.
 */
export function accountQuery() {
  return queryOptions({
    queryKey: queryKeys.account.all,
    queryFn: () => bootstrapAccount(),
    staleTime: Infinity,
    retry: 1,
  });
}

export interface SimpleEventsFilterDto {
  Sport: string;
  Categories?: string[];
  Tournaments?: string[];
  DateFrom?: string | null;
  DateTo?: string | null;
  EventIds?: number[];
  Hours?: number | null;
  Page?: number | null;
  Take: number;
}

export const EMPTY_LIVE_EVENTS_FILTER: SimpleEventsFilterDto = {
  Sport: '',
  Categories: [],
  Tournaments: [],
  EventIds: [],
  Take: 0,
};

const DEFAULT_LIVE_SPORTS_FILTERS: SimpleEventsFilterDto = EMPTY_LIVE_EVENTS_FILTER;

/** POST body for `api/v1/front/events/category`. */
export interface CategoryEventsFilterDto {
  Sport: string;
  Category: string;
  Page?: number | null;
  Take: number;
}

/** POST body for `api/v1/front/events/lastminute`. */
export interface LastMinuteFilterDto {
  Sport: string;
  Hours?: number | null;
  Page?: number | null;
  Take: number;
}

/** POST body for `api/v1/front/events/daterange`. */
export interface DateRangeEventsFilterDto {
  Sport?: string | null;
  Category?: string | null;
  Tournament?: string | null;
  DateFrom?: string | null;
  DateTo?: string | null;
  Page?: number | null;
  Take: number;
}

/** POST body for `api/v1/front/events/topevents`. */
export interface TopEventsFilterDto {
  Sport: string;
  EventIds: number[];
  Page?: number | null;
  Take: number;
}

export interface MainMarketDefinition {
  MarketId: number;
  Key: string;
  HasSpecifier: boolean;
  Outcomes: string[];
  DefaultOutcomes: string[];
  Additional: boolean;
}

export interface EventOddDto {
  Outcome: string;
  Value: number;
  Date?: string;
  Active: boolean;
}

export interface EventMarketLineDto {
  Specifier?: string;
  Active?: boolean;
  Main?: boolean;
  Odds: EventOddDto[];
}

export interface EventMarketDto {
  Id?: number;
  Key: string;
  Group?: string;
  Active?: boolean;
  Lines: EventMarketLineDto[];
}

export interface EventRowDto {
  SportId: number;
  CategoryId: number;
  TournamentId?: number | null;
  EventId: number;
  Active: boolean;
  Live: boolean;
  Finished: boolean;
  MatchStatus?: number | null;
  Statistics?: string | null;
  Time?: string | null;
  HomeScore?: number | null;
  AwayScore?: number | null;
  Period?: string | null;
  PeriodShort?: string | null;
  PeriodScore?: string | null;
  MarketsString?: string | null;
  MarketsCount: number;
  LastEdit?: string | null;
  ExpireOdds?: string | null;
  EventBId: number;
  /** Prematch Bet Builder offer flag from SportsAPI / CBAPI sync. */
  BetBuilderAvailable?: boolean;
}

export interface EventRowExtendedDto extends EventRowDto {
  Sport?: string | null;
  Category?: string | null;
  Tournament?: string | null;
  Home?: string | null;
  Away?: string | null;
  Date: string;
  Code?: string | null;
  Status: number;
  Top: boolean;
  LiveStream: boolean;
  Position: number;
  Blocked: boolean;
  MatchTrackerEnabled: boolean;
}

export interface DateEvents {
  Live: boolean;
  Date?: string | null;
  Events: EventRowExtendedDto[];
}

/** API events table — flat `Events` (tournament list; category/top under `Data`). */
export interface EventsTable {
  Live: boolean;
  Top: boolean;
  Position: number;
  Sport?: string | null;
  SportId: number;
  CategoryId: number;
  Category?: string | null;
  Tournament?: string | null;
  TournamentId?: number | null;
  Blocked: boolean;
  MarketHeaders: MainMarketDefinition[];
  Events: EventRowExtendedDto[];
}

/** Alias for the same flat-Events API table shape. */
export type TournamentEventsTable = EventsTable;

export interface PagingInfo {
  NumberOfPages: number;
  NumberOfResults: number;
  NumberOfResultsToDisplay: number;
  PageNumber: number;
  TotalNumberOfResults: number;
}

export interface PagedEventsTable {
  Data: EventsTable[];
  PagesInfo: PagingInfo;
}

/** Page size for category event feed (load-on-scroll). */
export const EVENTS_PAGE_SIZE = 10;
/** Top Events feed: at most 3 scroll pages (30 events with {@link EVENTS_PAGE_SIZE}). */
export const TOP_EVENTS_MAX_PAGES = 3;

export type CategoryEventsPage = {
  tables: EventsTable[];
  eventCount: number;
  page: number;
  numberOfPages?: number;
};

/** Local calendar day key (YYYY-MM-DD) for grouping prematch events. */
function calendarDayKey(dateValue: string): string {
  const date = parseApiUtcDate(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Groups a flat event list into `DateEvents` sections:
 * live first, then prematch by calendar day ascending.
 */
export function groupEventsByLiveAndDate(events: EventRowExtendedDto[]): DateEvents[] {
  const liveEvents: EventRowExtendedDto[] = [];
  const byDate = new Map<string, EventRowExtendedDto[]>();

  for (const event of events) {
    if (event.Live) {
      liveEvents.push(event);
      continue;
    }
    const key = calendarDayKey(event.Date);
    const bucket = byDate.get(key);
    if (bucket) {
      bucket.push(event);
    } else {
      byDate.set(key, [event]);
    }
  }

  const groups: DateEvents[] = [];
  if (liveEvents.length > 0) {
    groups.push({ Live: true, Date: null, Events: liveEvents });
  }

  const sortedDays = [...byDate.keys()].sort();
  for (const day of sortedDays) {
    const dayEvents = byDate.get(day) ?? [];
    groups.push({
      Live: false,
      Date: dayEvents[0]?.Date ?? day,
      Events: dayEvents,
    });
  }

  return groups;
}

function countEventsInTables(tables: EventsTable[]): number {
  return tables.reduce((total, table) => total + (table.Events?.length ?? 0), 0);
}

/** Normalize paged category / top-events responses (flat `Events` tables). */
function normalizePagedEventsResponse(data: unknown, page: number): CategoryEventsPage {
  if (Array.isArray(data)) {
    const tables = data as EventsTable[];
    return { tables, eventCount: countEventsInTables(tables), page };
  }

  if (data && typeof data === 'object' && Array.isArray((data as { Data?: unknown }).Data)) {
    const wrapped = data as {
      Data: EventsTable[];
      PagesInfo?: Partial<PagingInfo>;
    };
    return {
      tables: wrapped.Data,
      eventCount: countEventsInTables(wrapped.Data),
      page: wrapped.PagesInfo?.PageNumber ?? page,
      numberOfPages: wrapped.PagesInfo?.NumberOfPages,
    };
  }

  return { tables: [], eventCount: 0, page };
}

function normalizeTournamentEventsList(data: unknown): EventsTable[] {
  if (!Array.isArray(data)) return [];
  return data as EventsTable[];
}

/** Tournament events request — no paging fields. */
export type TournamentEventsFilter = {
  Sport: string;
  Categories?: string[];
  Tournaments?: string[];
  DateFrom?: string | null;
  DateTo?: string | null;
  EventIds?: number[];
};

export type CategoryEventsFilter = {
  Sport: string;
  Category: string;
  Take?: number;
};

/** Tournament events — `POST api/v1/front/events/tournament` (full list, no paging). */
export function tournamentEventsQuery(filters: TournamentEventsFilter) {
  const body: TournamentEventsFilter = {
    Sport: filters.Sport,
    Categories: filters.Categories ?? [],
    Tournaments: filters.Tournaments ?? [],
    DateFrom: filters.DateFrom ?? null,
    DateTo: filters.DateTo ?? null,
    EventIds: filters.EventIds ?? [],
  };

  return queryOptions({
    queryKey: queryKeys.sports.eventsTournament(body as unknown as QueryFilters),
    queryFn: async ({ signal }) => {
      const data = await api.post<unknown>(
        endpoints.sports.eventsTournament.host,
        endpoints.sports.eventsTournament.path,
        body as unknown as Record<string, unknown>,
        signal,
      );
      return normalizeTournamentEventsList(data);
    },
    staleTime: 30_000,
  });
}

export type TopEventItem = {
  event: EventRowExtendedDto;
  marketHeaders: MainMarketDefinition[];
};

function flattenEventsTables(tables: EventsTable[]): TopEventItem[] {
  const items: TopEventItem[] = [];
  for (const table of tables) {
    for (const event of table.Events ?? []) {
      items.push({
        event: {
          ...event,
          Sport: event.Sport ?? table.Sport,
          Category: event.Category ?? table.Category,
          Tournament: event.Tournament ?? table.Tournament,
        },
        marketHeaders: table.MarketHeaders,
      });
    }
  }
  return items;
}

export type TopEventsFilter = {
  Sport: string;
  EventIds?: number[];
  Page?: number | null;
  Take?: number;
};

/** Top Events preview — `POST api/v1/front/events/topevents` (single page, flattened). */
export function topEventsQuery(filters: TopEventsFilter = { Sport: 'Football' }) {
  const eventIds = filters.EventIds ?? [];
  const take = filters.Take ?? Math.max(EVENTS_PAGE_SIZE, eventIds.length || EVENTS_PAGE_SIZE);
  const body: TopEventsFilterDto = {
    Sport: filters.Sport,
    EventIds: eventIds,
    Page: filters.Page ?? 0,
    Take: take,
  };

  return queryOptions({
    queryKey: queryKeys.sports.topEvents(body as unknown as QueryFilters),
    queryFn: async ({ signal }) => {
      const data = await api.post<unknown>(
        endpoints.sports.topEvents.host,
        endpoints.sports.topEvents.path,
        body as unknown as Record<string, unknown>,
        signal,
      );
      return flattenEventsTables(normalizePagedEventsResponse(data, body.Page ?? 0).tables);
    },
    staleTime: 30_000,
  });
}

/** Top Events feed — `POST api/v1/front/events/topevents` with infinite scroll. */
export function topEventsInfiniteQuery(filters: TopEventsFilter) {
  const take = filters.Take ?? EVENTS_PAGE_SIZE;
  const sport = filters.Sport.trim();
  const eventIds = filters.EventIds ?? [];

  return infiniteQueryOptions({
    queryKey: queryKeys.sports.topEvents({
      Sport: sport,
      EventIds: eventIds,
      Take: take,
      infinite: true,
    }),
    queryFn: async ({ pageParam, signal }) => {
      const body: TopEventsFilterDto = {
        Sport: sport,
        EventIds: eventIds,
        Page: pageParam,
        Take: take,
      };
      const data = await api.post<unknown>(
        endpoints.sports.topEvents.host,
        endpoints.sports.topEvents.path,
        body as unknown as Record<string, unknown>,
        signal,
      );
      return normalizePagedEventsResponse(data, pageParam);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      if (nextPage >= TOP_EVENTS_MAX_PAGES) return undefined;
      if (lastPage.numberOfPages != null) {
        return nextPage < lastPage.numberOfPages ? nextPage : undefined;
      }
      return lastPage.eventCount < take ? undefined : nextPage;
    },
    staleTime: 30_000,
  });
}

/** Category feed — `POST api/v1/front/events/category` with {@link CategoryEventsFilterDto}. */
export function categoryEventsInfiniteQuery(filters: CategoryEventsFilter) {
  const take = filters.Take ?? EVENTS_PAGE_SIZE;
  const sport = filters.Sport.trim();
  const category = filters.Category.trim();

  return infiniteQueryOptions({
    queryKey: queryKeys.sports.eventsCategory({
      Sport: sport,
      Category: category,
      Take: take,
      infinite: true,
    }),
    queryFn: async ({ pageParam, signal }) => {
      const body: CategoryEventsFilterDto = {
        Sport: sport,
        Category: category,
        Page: pageParam,
        Take: take,
      };
      const data = await api.post<unknown>(
        endpoints.sports.eventsCategory.host,
        endpoints.sports.eventsCategory.path,
        body as unknown as Record<string, unknown>,
        signal,
      );
      return normalizePagedEventsResponse(data, pageParam);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.numberOfPages != null) {
        return lastPage.page + 1 < lastPage.numberOfPages ? lastPage.page + 1 : undefined;
      }
      return lastPage.eventCount < take ? undefined : lastPage.page + 1;
    },
    staleTime: 30_000,
  });
}

/** Client filter for last-minute event lists. */
export type LastMinuteFilter = {
  Sport: string;
  Hours?: number | null;
  Page?: number | null;
  Take?: number;
};

/** Last-minute preview — `POST api/v1/front/events/lastminute` (single page). */
export function lastMinuteQuery(filters: LastMinuteFilter) {
  const take = filters.Take ?? EVENTS_PAGE_SIZE;
  const sport = filters.Sport.trim();
  const body: LastMinuteFilterDto = {
    Sport: sport,
    Hours: filters.Hours ?? null,
    Page: filters.Page ?? 0,
    Take: take,
  };

  return queryOptions({
    queryKey: queryKeys.sports.lastMinute(body as unknown as QueryFilters),
    queryFn: async ({ signal }) => {
      const data = await api.post<unknown>(
        endpoints.sports.lastMinute.host,
        endpoints.sports.lastMinute.path,
        body as unknown as Record<string, unknown>,
        signal,
      );
      return normalizePagedEventsResponse(data, body.Page ?? 0).tables;
    },
    staleTime: 30_000,
  });
}

/** Last-minute feed — `POST api/v1/front/events/lastminute` with infinite scroll. */
export function lastMinuteInfiniteQuery(filters: LastMinuteFilter) {
  const take = filters.Take ?? EVENTS_PAGE_SIZE;
  const sport = filters.Sport.trim();
  const hours = filters.Hours ?? null;

  return infiniteQueryOptions({
    queryKey: queryKeys.sports.lastMinute({
      Sport: sport,
      Hours: hours,
      Take: take,
      infinite: true,
    }),
    queryFn: async ({ pageParam, signal }) => {
      const body: LastMinuteFilterDto = {
        Sport: sport,
        Hours: hours,
        Page: pageParam,
        Take: take,
      };
      const data = await api.post<unknown>(
        endpoints.sports.lastMinute.host,
        endpoints.sports.lastMinute.path,
        body as unknown as Record<string, unknown>,
        signal,
      );
      return normalizePagedEventsResponse(data, pageParam);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.numberOfPages != null) {
        return lastPage.page + 1 < lastPage.numberOfPages ? lastPage.page + 1 : undefined;
      }
      return lastPage.eventCount < take ? undefined : lastPage.page + 1;
    },
    staleTime: 30_000,
  });
}

export type DateRangeEventsFilter = {
  Sport?: string | null;
  Category?: string | null;
  Tournament?: string | null;
  DateFrom?: string | null;
  DateTo?: string | null;
  Page?: number | null;
  Take?: number;
};

function toDateRangeBody(filters: DateRangeEventsFilter, page: number, take: number): DateRangeEventsFilterDto {
  return {
    Sport: filters.Sport?.trim() || null,
    Category: filters.Category?.trim() || null,
    Tournament: filters.Tournament?.trim() || null,
    DateFrom: filters.DateFrom ?? null,
    DateTo: filters.DateTo ?? null,
    Page: page,
    Take: take,
  };
}

/** Today / date-range preview — `POST api/v1/front/events/daterange` (single page). */
export function dateRangeQuery(filters: DateRangeEventsFilter) {
  const take = filters.Take ?? EVENTS_PAGE_SIZE;
  const body = toDateRangeBody(filters, filters.Page ?? 0, take);

  return queryOptions({
    queryKey: queryKeys.sports.dateRange(body as unknown as QueryFilters),
    queryFn: async ({ signal }) => {
      const data = await api.post<unknown>(
        endpoints.sports.dateRange.host,
        endpoints.sports.dateRange.path,
        body as unknown as Record<string, unknown>,
        signal,
      );
      return normalizePagedEventsResponse(data, body.Page ?? 0).tables;
    },
    staleTime: 30_000,
  });
}

/** Today / date-range feed — `POST api/v1/front/events/daterange` with infinite scroll. */
export function dateRangeInfiniteQuery(filters: DateRangeEventsFilter) {
  const take = filters.Take ?? EVENTS_PAGE_SIZE;
  const sport = filters.Sport?.trim() || null;
  const category = filters.Category?.trim() || null;
  const tournament = filters.Tournament?.trim() || null;
  const dateFrom = filters.DateFrom ?? null;
  const dateTo = filters.DateTo ?? null;

  return infiniteQueryOptions({
    queryKey: queryKeys.sports.dateRange({
      Sport: sport,
      Category: category,
      Tournament: tournament,
      DateFrom: dateFrom,
      DateTo: dateTo,
      Take: take,
      infinite: true,
    }),
    queryFn: async ({ pageParam, signal }) => {
      const body = toDateRangeBody(
        {
          Sport: sport,
          Category: category,
          Tournament: tournament,
          DateFrom: dateFrom,
          DateTo: dateTo,
        },
        pageParam,
        take,
      );
      const data = await api.post<unknown>(
        endpoints.sports.dateRange.host,
        endpoints.sports.dateRange.path,
        body as unknown as Record<string, unknown>,
        signal,
      );
      return normalizePagedEventsResponse(data, pageParam);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.numberOfPages != null) {
        return lastPage.page + 1 < lastPage.numberOfPages ? lastPage.page + 1 : undefined;
      }
      return lastPage.eventCount < take ? undefined : lastPage.page + 1;
    },
    staleTime: 30_000,
  });
}

export interface EventDetailsDto {
  Live: boolean;
  EventId: number;
  Sport?: string | null;
  SportId: number;
  Category?: string | null;
  CategoryId: number;
  TournamentId: number;
  Tournament?: string | null;
  RiskLevel?: number | null;
  Home?: string | null;
  HomeId?: number | null;
  Away?: string | null;
  AwayId?: number | null;
  Date?: string | null;
  Code?: string | null;
  Statistics?: string | null;
  HomeScore: number;
  AwayScore: number;
  Time?: string | null;
  PeriodScore?: string | null;
  Period?: string | null;
  PeriodShort?: string | null;
  MarketsString?: string | null;
  LastEdit?: string | null;
  ExpireOdds?: string | null;
  Status: number;
  Active: boolean;
  LiveStream: boolean;
  WidgetLink?: string | null;
  LiveStreamLink?: string | null;
  TheSportsId?: string | null;
  MarketGroups?: string[] | null;
  EventBId: number;
  MatchTrackerEnabled: boolean;
  PlayersList?: string | null;
  /** Prematch Bet Builder offer flag from SportsAPI / CBAPI sync. */
  BetBuilderAvailable?: boolean;
}

export function eventDetailsQuery(eventId: string) {
  return queryOptions({
    queryKey: queryKeys.sports.event(eventId),
    queryFn: ({ signal }) =>
      fetchFrom<EventDetailsDto>(endpoints.sports.eventDetails(eventId), {}, signal),
    staleTime: 15_000,
  });
}

/**
 * Bet Builder markets for a prematch event — `GET api/v1/front/events/{eventId}/betbuilder`.
 * Same shape as event details; markets filtered to CBAPI available-selections.
 * Empty `MarketsString` `"[]"` is a soft success (CustomBets down / no match).
 */
export function eventBetBuilderQuery(eventId: string) {
  return queryOptions({
    queryKey: queryKeys.sports.betBuilder(eventId),
    queryFn: ({ signal }) =>
      fetchFrom<EventDetailsDto>(endpoints.sports.betBuilder(eventId), {}, signal),
    staleTime: 15_000,
  });
}

/** Live event details — `GET api/v1/front/live/{eventId}`. */
export function liveEventDetailsQuery(eventId: string) {
  return queryOptions({
    queryKey: queryKeys.live.event(eventId),
    queryFn: ({ signal }) =>
      fetchFrom<EventDetailsDto>(endpoints.live.eventDetails(eventId), {}, signal),
    staleTime: 15_000,
  });
}

export interface LiveTournamentMenuItem {
  Id: number;
  Name: string;
  Position: number;
  Blocked: boolean;
  SportId: number;
  Sport: string;
  CategoryId: number;
  Category: string;
  Count: number;
}

function buildSimpleEventsFilterBody(filters: SimpleEventsFilterDto): SimpleEventsFilterDto {
  return {
    Sport: filters.Sport,
    Categories: filters.Categories ?? [],
    Tournaments: filters.Tournaments ?? [],
    DateFrom: filters.DateFrom ?? null,
    DateTo: filters.DateTo ?? null,
    EventIds: filters.EventIds ?? [],
    Hours: filters.Hours ?? null,
    Page: filters.Page ?? null,
    Take: filters.Take,
  };
}

/** Live events tables — `POST api/v1/front/live/tables` with {@link SimpleEventsFilterDto}. */
export function liveEventsTablesQuery(filters: SimpleEventsFilterDto) {
  const body = buildSimpleEventsFilterBody(filters);

  return queryOptions({
    queryKey: queryKeys.live.tables(body as unknown as QueryFilters),
    queryFn: async ({ signal }) => {
      const data = await api.post<unknown>(
        endpoints.live.tables.host,
        endpoints.live.tables.path,
        body as unknown as Record<string, unknown>,
        signal,
      );
      return normalizeTournamentEventsList(data);
    },
    staleTime: 5_000,
  });
}

/** Live tournament filter — `POST api/v1/front/live/tournaments` with {@link SimpleEventsFilterDto}. */
export function liveTournamentsQuery(filters: SimpleEventsFilterDto) {
  const body = buildSimpleEventsFilterBody(filters);

  return queryOptions({
    queryKey: queryKeys.live.tournaments(body as unknown as QueryFilters),
    queryFn: ({ signal }) =>
      api.post<LiveTournamentMenuItem[]>(
        endpoints.live.tournaments.host,
        endpoints.live.tournaments.path,
        body as unknown as Record<string, unknown>,
        signal,
      ),
    staleTime: 5_000,
  });
}

/** Live sports menu — `POST api/v1/front/live/sports` with {@link SimpleEventsFilterDto}. */
export function liveSportsMenuQuery(filters: SimpleEventsFilterDto = DEFAULT_LIVE_SPORTS_FILTERS) {
  return queryOptions({
    queryKey: queryKeys.live.sports(filters as unknown as QueryFilters),
    queryFn: ({ signal }) =>
      api.post<SportMenuItem[]>(
        endpoints.live.sports.host,
        endpoints.live.sports.path,
        filters as unknown as Record<string, unknown>,
        signal,
      ),
    staleTime: 5_000,
  });
}

export interface TranslationsResponse {
  WebsiteTranslates: Record<string, string>;
  MarketTranslates: Record<string, string>;
  OutcomeTranslates: Record<string, string>;
  SportsTranslates: Record<string, string>;
}

export function translationsQuery(accountId: string, language: string) {
  return queryOptions({
    queryKey: queryKeys.account.translations(accountId, language),
    queryFn: ({ signal }) =>
      api.post<TranslationsResponse>(
        endpoints.translations.all.host,
        endpoints.translations.all.path,
        { TableFilters: { AccountId: accountId, Language: language } },
        signal,
      ),
    staleTime: Infinity,
  });
}

export function cmsPagesQuery(accountGuid: string, language: string) {
  return queryOptions({
    queryKey: queryKeys.cms.pages(accountGuid, language),
    queryFn: ({ signal }) =>
      api.post<CmsPagesListResponse>(
        endpoints.cms.pages.host,
        endpoints.cms.pages.path,
        {
          TableFilters: { AccountGuid: accountGuid, Language: language },
          Sorting: { SortBy: 'DisplayIndex', Direction: 'asc' },
        },
        signal,
      ),
    staleTime: 15 * 60_000,
  });
}

/** CMS page details — legacy `POST sb/api/pages/details` with AccountGuid / Url / Language. */
export function cmsPageQuery(accountGuid: string, slug: string, language: string) {
  const url = slug.trim();
  return queryOptions({
    queryKey: queryKeys.cms.page(url),
    queryFn: ({ signal }) =>
      api.post<CmsPage>(
        endpoints.cms.page.host,
        endpoints.cms.page.path,
        { AccountGuid: accountGuid, Url: url, Language: language },
        signal,
      ),
    enabled: Boolean(accountGuid && url && language),
    staleTime: 10 * 60_000,
  });
}

/**
 * SportsAPI2 search — Categories / Tournaments / Events
 * (`POST api/v1/front/search`).
 */
export function sportsSearchQuery(term: string) {
  const trimmed = term.trim();
  return queryOptions({
    queryKey: queryKeys.sports.search(trimmed),
    queryFn: async ({ signal }) => {
      const accountGuid = await getAccountId();
      return api.post<SportsSearchResponse>(
        endpoints.sports.search.host,
        endpoints.sports.search.path,
        { Data: trimmed, AccountGuid: accountGuid },
        signal,
      );
    },
    enabled: trimmed.length >= SPORTS_SEARCH_MIN_LENGTH,
    staleTime: 30_000,
  });
}
